export enum Phase {
  Zero = 'PhaseZero',
  One = 'PhaseOne',
  Two = 'PhaseTwo',
  Three = 'PhaseThree',
  Four = 'PhaseFour',
}

type PhaseSummary = {
  average_ayble_score?: number;
  days_logged?: number;
  end_date?: string;
  longest_streak?: number;
  lowest_ayble_score?: number;
  most_eaten_food?: string;
  phase_extended?: boolean;
  start_date?: string;
};

export enum CareModel {
  Collaborative = 'Collaborative',
  CoachOnly = 'Coach-only',
  DigitalOnly = 'Digital-only',
}

/**
 * Risk stratification level — the canonical definition, re-exported by
 * `RiskDots` (src/components/PatientProfile/RiskDots.tsx) so the indicator and
 * the data agree by construction.
 *
 * Deliberately has no "N/A" member: not-yet-rated is the absence of a level,
 * so it's `null` at the field (see `MockedRosterFields.risk_stratification`)
 * and each consumer decides how to render it. Folding null into the type made
 * every `RiskDots` call site look like it had to handle a state the component
 * can't draw.
 */
export type RiskLevel = 'low' | 'moderate' | 'high';

/**
 * Where a member sits in the care-coordination funnel, per the CareOS Filters
 * Redesign brief. Distinct from `AppStatus` in models/careOSPrototype.ts —
 * that one is a demo-wide toggle for the Patient Profile's Gut Checks card,
 * this varies per patient across the roster.
 *
 * No `'new'` member: the brief defines "New" by signup recency (signed up in
 * the last 7 days), not by funnel position, so it isn't mutually exclusive
 * with these — a member can be newly signed up AND never logged in at once.
 * That's derived from `created_at` instead (see `filters.ts`'s
 * `isRecentSignup`) rather than folded into this enum, which is why care
 * team feedback (Aug 2026) found selecting "New" + "Never Logged In" didn't
 * narrow to their intersection: both were living in the same OR'd enum.
 *
 * `'ongoing_care'` (added Aug 2026 round 4) *is* a legitimate member of this
 * enum, unlike `'new'` — it's the state a member enters once their initial
 * visit is behind them and they're working a care plan, so it's genuinely
 * mutually exclusive with every stage before it. It's also the only stage
 * where an outcome goal means anything: before it there are no care plan
 * blocks to measure symptom change against, and no elapsed time to read a
 * trend off the baseline survey (see `OutcomeGoalStatus`'s doc comment for
 * where that distinction currently does — and doesn't — get used). Note a
 * member in ongoing care needn't have a visit on the books — the two are
 * independent.
 */
export type CareCoordinationStatus =
  | 'never_logged_in'
  | 'onboarding'
  | 'baseline_complete'
  | 'booked_initial_visit'
  | 'initial_visit_occurred'
  | 'ongoing_care'
  | 'cancelled';

/**
 * Progress toward a member's outcome goal. Added round 4 (Aug 2026) so a care
 * team could filter straight to who's declining without opening every chart;
 * round 8 removed that filter (and it was never a roster column), so this is
 * currently a real field with nothing in the UI reading it. Independent of
 * `CareCoordinationStatus`: a member can be fully onboarded and still be
 * worsening, or newly onboarding and already improving.
 */
export type OutcomeGoalStatus =
  | 'major_improvement'
  | 'moderate_improvement'
  | 'no_change'
  | 'worsening';

/**
 * Condition grouping the roster filters on, per the brief's "Condition — GI,
 * Autoimmune, Weight Management" replacement for the old diagnosis filter.
 * Coarser than `diagnosis` (which stays on the row for display) and unrelated
 * to `DiagnosisCategory` in api/types, whose buckets are IBD / IBS / UpperGI.
 */
export type ConditionCategory = 'gi' | 'autoimmune' | 'weight_management';

/**
 * Fields the CareOS roster filters and columns need that the real
 * `GET /private/coach/users` contract does not return yet.
 *
 * Deliberately kept out of `User` so the boundary stays legible: everything in
 * `User` is real API contract, everything here is prototype-only fixture data
 * backing the filter sidebar (risk stratification, the RD/pharmacist care-team
 * split, care-coordination status, employer / referring UTM, and a messaging
 * activity signal the backend has no equivalent for at all).
 *
 * When these land server-side, fold them into `User` and delete this type.
 */
export type MockedRosterFields = {
  rd_first_name?: string;
  rd_last_name?: string;
  pharmacist_first_name?: string;
  pharmacist_last_name?: string;
  /** `null` means nobody has rated them yet, which renders as "N/A". */
  risk_stratification?: RiskLevel | null;
  care_coordination_status?: CareCoordinationStatus;
  outcome_goal_status?: OutcomeGoalStatus;
  /**
   * When the member last did anything in the app, ISO timestamp. Surfaced in
   * the roster's Care Coordination column for members in ongoing care, whose
   * tag reads "Last Engaged · <date>".
   *
   * Deliberately its own field rather than reuse of `last_gut_check` or
   * `last_mindgut_activity` on `User`: those are two specific features, and
   * "engaged with the app at all" is a broader signal than either. The
   * fixtures currently seed all three off one activity date, but a real
   * backend would compute this across every surface, so the roster shouldn't
   * be reading a feature-specific timestamp and calling it engagement.
   */
  last_app_engagement?: string;
  referring_utm?: string;
  /** A member can carry more than one, so the filter matches on overlap. */
  condition_categories?: ConditionCategory[];
};

/** A roster row: the real user contract plus the prototype-only filter fields. */
export type RosterPatient = User & MockedRosterFields;

export type User = {
  id: string;
  affiliate?: string;
  auth_id: string;
  // When populated, it is the date the user canceled
  canceled?: string;
  next_meeting?: string;
  care_model: CareModel;
  coach_first_name?: string;
  coach_last_name?: string;
  provider_first_name?: string;
  provider_last_name?: string;
  email: string;
  phase_1_summary: PhaseSummary | null;
  phase_2_summary: PhaseSummary | null;
  phase_3_summary: PhaseSummary | null;
  phase_4_summary: PhaseSummary | null;
  phone_number?: string;
  birthdate: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  current_phase: Phase;
  diagnosis: string;
  phase_start_date: string; // date format
  days_of_phase: number;
  days_of_current_streak: number;
  in_transition: boolean;
  sex: string;
  gender: string;
  healthie_id?: number;
  ethnicity: string;
  onboarded: boolean;
  gut_break: boolean;
  referrer?: string;
  rest_day: boolean;
  on_pause: boolean;
  diagnosis_secondary: string;
  diagnosis_ids: string[];
  allergens: string[];
  allergen_ids: number[];
  diet_restrictions: string[];
  dietary_restriction_ids: number[];
  selected_foods: string[];
  selected_food_ids: number[];
  other_diet_ids: number[];
  recommended_foods: string[];
  current_symptoms: string[];
  potential_triggers: string[];
  plan_name?: string;
  suspected_triggers: string[];
  verified_triggers: string[];
  sifter_favorites: string[];
  customer_id: string;
  subscription_id: string;
  iap_purchase_id: string;
  goals: string[];
  coach_id: string;
  intercom_id: string;
  created_at: string;
  modified_at: string;
  medications?: string;
  opt_in_marketing?: boolean;
  opt_in_news?: boolean;
  user_phase_progress?: number;
  last_mindgut_activity?: string;
  user_type: string;
  health_plan?: string;
  health_plan_member_id: string | null;
  health_system?: string;
  pregnant_when_onboarded: null | 'Currently' | 'No' | 'Nursing' | 'Planning';
  history_of_disordered_eating?: boolean;
  history_of_gi_surgery?: boolean;
  timezone: string;
  amount_completed_gut_checks: number | null;
  last_gut_check: string | null;
};
